import { Spin } from 'antd';
import { Suspense, lazy } from 'react';

import { useParams } from '@suntex.core/nav';
import {
  $ArithmeticOperation,
  $GymMode,
  $ModuleInfo,
  ArithmeticOperation,
  GymMode,
  ModuleInfo,
} from '@suntex.fim/enums';
import { useModuleById } from '@suntex.fim/framework';
import { modulesPath } from '@suntex.fim/nav';
import { GymGameNumber, gymGameSubtitle } from '@suntex.fim/utils';

import { ModuleLayout } from '../components/ModuleLayout';

import { SOUND_DIRECTORY, useModuleViewProps } from './shared';

const GymView = lazy(() =>
  import('@suntex.modules/gyms').then((m) => ({ default: m.GymView })),
);

function gymOpBase(mode: GymMode): ModuleInfo {
  switch (mode) {
    case GymMode.WholeNumbers:
      return ModuleInfo.gym_num_add;
    case GymMode.Fractions:
      return ModuleInfo.gym_frc_add;
    case GymMode.Decimals:
      return ModuleInfo.gym_dec_add;
    case GymMode.Integers:
      return ModuleInfo.gym_int_add;
    case GymMode.K2:
      return ModuleInfo.gym_k2_add;
    default:
      return $GymMode.throwInvalid(mode);
  }
}

function gymsModuleId(mode: GymMode, op: ArithmeticOperation): ModuleInfo {
  return $ModuleInfo.from(gymOpBase(mode) - 1 + op);
}

function tryFromSlug<T>(
  ctx: { fromSlug: (slug: string) => T },
  slug: string | undefined,
): T | null {
  if (!slug) return null;
  try {
    return ctx.fromSlug(slug);
  } catch {
    return null;
  }
}

export function Gyms() {
  const params = useParams<{ mode: string; op: string; game: string }>();
  const viewProps = useModuleViewProps();

  const mode = tryFromSlug($GymMode, params.mode);
  const op = tryFromSlug($ArithmeticOperation, params.op);
  const game = Number(params.game);

  const steps = mode !== null ? $GymMode.describe(mode).steps : 0;
  const isValid =
    mode !== null &&
    op !== null &&
    Number.isInteger(game) &&
    game >= 1 &&
    game <= steps;
  const moduleId = isValid ? gymsModuleId(mode, op) : null;

  // Hooks must be called unconditionally; pass 0 sentinel when invalid
  const module = useModuleById(moduleId ?? 0);

  if (!isValid || moduleId === null) {
    return <div>Gym game not found.</div>;
  }

  if (!module) {
    return <Spin />;
  }

  const modeSlug = $GymMode.describe(mode).slug;
  const opSlug = $ArithmeticOperation.describe(op).slug;

  const nextGameUrl =
    game < steps
      ? `${modulesPath(viewProps.preview)}/gym/${modeSlug}/${opSlug}/${game + 1}`
      : null;

  return (
    <ModuleLayout
      moduleId={moduleId}
      title={`${$GymMode.describe(mode).label} Gym`}
      subtitle={gymGameSubtitle(mode, op, game as GymGameNumber)}
    >
      <Suspense fallback={<Spin />}>
        <GymView
          {...viewProps}
          module={module}
          gymMode={mode}
          arithmeticOperation={op}
          gameNumber={game}
          soundDirectory={SOUND_DIRECTORY}
          nextGameUrl={nextGameUrl}
        />
      </Suspense>
    </ModuleLayout>
  );
}
